/**
 * Content module — surface 2.2 "Portfolio health".
 *
 * Every figure, label, route and derived statement this surface renders is
 * resolved HERE from `@/demo/data`; the page component holds no literals
 * (build brief §0a). The deck states four headline figures (avg days per
 * phase, on-time gates, scope change requests, rework after go-live) and one
 * claim — that client-owned reasons carry four in five of the delay days.
 * The per-phase series and the stall register the deck does NOT enumerate are
 * authored ONCE below, and are authored so the deck's own totals fall out of
 * them: the medians average to 14 now / 23 before, and the client rows really
 * do sum to 80% of the delay days. Nothing on this page is typed twice.
 */
import type { StatusTone } from '@/components/px/StatusPill'
import { PHASES, PORTFOLIO, PROJECTS, thinSpace, type PhaseKey } from '@/demo/data'

/* ── chrome ─────────────────────────────────────────────────────────────── */

/** The reporting window every figure on this surface is measured over. */
export const WINDOW_DAYS = 90

export const MODULE_LABEL = 'PX · Projects'
export const BREADCRUMB = 'Portfolio › Health'
export const CONTEXT_PILL = `LAST ${WINDOW_DAYS} DAYS`
export const PAGE_TITLE = 'Portfolio health'
export const PAGE_LEDE =
  'Phase throughput, gate ageing and the reasons work stalls — the chart the group looks at before committing new dates.'

/* ── routes ─────────────────────────────────────────────────────────────── */

export const ROUTES = {
  board: '/projects',
  blueprint: '/project/van-dijk/blueprint',
  migration: '/project/van-dijk/migration',
  test: '/project/van-dijk/test',
  portal: '/portal',
} as const

/* ── formatting ─────────────────────────────────────────────────────────── */

/** Day counts in the page's voice: thin-space thousands, explicit unit. */
export const days = (n: number): string => `${thinSpace(n)} d`

/** A drop written as a percentage: 34 → 17 renders 50. */
export const dropPct = (before: number, now: number): number =>
  Math.round(((before - now) / before) * 100)

/** Signed shorthand for a reduction, with the true minus sign: "−17 d". */
export const drop = (n: number, unit: string): string => `\u2212${thinSpace(n)}${unit}`

/* ── cycle time by phase ────────────────────────────────────────────────── */

/**
 * Median calendar days a phase took, before the agents and now. Authored once,
 * per phase, so the KPI row can be *computed* from the series rather than
 * asserted next to it — the two can never disagree.
 */
const MEDIAN_DAYS: Readonly<Record<PhaseKey, { before: number; now: number }>> = {
  discover: { before: 18, now: 13 },
  design: { before: 21, now: 15 },
  build: { before: 34, now: 17 },
  migrate: { before: 29, now: 15 },
  test: { before: 26, now: 14 },
  'go-live': { before: 17, now: 12 },
  maintain: { before: 16, now: 12 },
}

/**
 * The patterned middle — the phases whose work is repeatable enough to hand to
 * agents, and therefore the phases that compressed. This is the claim the
 * insight rests on, so it is declared, not inferred from the numbers.
 */
export const PATTERNED_MIDDLE: readonly PhaseKey[] = ['build', 'migrate', 'test']

export const PATTERNED_TAG = 'agent-run'

export interface CycleRow {
  key: PhaseKey
  name: string
  subLabel: string
  gate: string
  before: number
  now: number
  /** Percentage of the longest bar on the chart — the shared bar scale. */
  beforePct: number
  nowPct: number
  dropDays: number
  dropPct: number
  dropLabel: string
  dropPctLabel: string
  patterned: boolean
}

const median = (key: PhaseKey): { before: number; now: number } => MEDIAN_DAYS[key]

const SCALE_MAX = Math.max(...PHASES.map((p) => median(p.key).before))

export const CYCLE_ROWS: readonly CycleRow[] = PHASES.map((phase) => {
  const { before, now } = median(phase.key)
  return {
    key: phase.key,
    name: phase.name,
    subLabel: phase.subLabel,
    gate: phase.gate,
    before,
    now,
    beforePct: Math.round((before / SCALE_MAX) * 100),
    nowPct: Math.round((now / SCALE_MAX) * 100),
    dropDays: before - now,
    dropPct: dropPct(before, now),
    dropLabel: drop(before - now, ' d'),
    dropPctLabel: drop(dropPct(before, now), '%'),
    patterned: PATTERNED_MIDDLE.includes(phase.key),
  }
})

const mean = (xs: readonly number[]): number =>
  Math.round(xs.reduce((sum, x) => sum + x, 0) / xs.length)

/** The deck's "14 (was 23)" — read off the series, never typed beside it. */
export const AVG_DAYS_NOW = mean(CYCLE_ROWS.map((r) => r.now))
export const AVG_DAYS_BEFORE = mean(CYCLE_ROWS.map((r) => r.before))

export const CYCLE_LABEL = 'Cycle time by phase'
export const CYCLE_LEDE = `Median calendar days per phase, now against the ${WINDOW_DAYS}-day window before the agents ran. The patterned middle — ${PATTERNED_MIDDLE.length} phases whose work repeats across engagements — carries the compression.`
export const BAR_NOW_LABEL = 'now'
export const BAR_BEFORE_LABEL = 'before'
export const CYCLE_LIST_LABEL = 'Median days per phase, now against before'

/* ── why work stalls ────────────────────────────────────────────────────── */

export type StallOwner = 'Client' | 'Perpleks'

/** Owner is a pill AND a word — the tone never carries the meaning alone. */
export const ownerTone: Record<StallOwner, StatusTone> = {
  Client: 'review',
  Perpleks: 'neutral',
}

interface StallSeed {
  id: string
  reason: string
  detail: string
  occurrences: number
  avgDelayDays: number
  owner: StallOwner
  /** The surface that owns the fix, where one exists. */
  fix?: { label: string; to: string }
}

const STALL_SEEDS: readonly StallSeed[] = [
  {
    id: 'decision-unanswered',
    reason: 'Client decision unanswered (design)',
    detail: 'a blueprint decision sits open past its by-date',
    occurrences: 23,
    avgDelayDays: 6,
    owner: 'Client',
    fix: { label: 'Blueprint decisions', to: ROUTES.blueprint },
  },
  {
    id: 'data-delivery-late',
    reason: 'Client data delivery late (migration)',
    detail: 'an extract arrives after the trial-load slot',
    occurrences: 17,
    avgDelayDays: 9,
    owner: 'Client',
    fix: { label: 'Migration data', to: ROUTES.migration },
  },
  {
    id: 'uat-dates-unconfirmed',
    reason: 'UAT dates unconfirmed',
    detail: 'testers are named but not booked',
    occurrences: 11,
    avgDelayDays: 7,
    owner: 'Client',
    fix: { label: 'Test & UAT', to: ROUTES.test },
  },
  {
    id: 'internal-review-backlog',
    reason: 'Internal review backlog',
    detail: 'agent output waits on a reviewer with no slot',
    occurrences: 14,
    avgDelayDays: 3,
    owner: 'Perpleks',
  },
  {
    id: 'connector-access',
    reason: 'Third-party connector access',
    detail: 'a vendor credential or sandbox is chased by us',
    occurrences: 6,
    avgDelayDays: 5,
    owner: 'Perpleks',
  },
  {
    id: 'environment-rebuild',
    reason: 'Environment rebuild before a trial load',
    detail: 'a staging refresh blocks the next rehearsal',
    occurrences: 5,
    avgDelayDays: 4,
    owner: 'Perpleks',
  },
]

export interface StallRow extends StallSeed {
  /** occurrences × average delay — the days this reason actually cost. */
  delayDays: number
  /** Share of all delay days, for the bar behind the figure. */
  sharePct: number
}

const TOTAL_DELAY_DAYS = STALL_SEEDS.reduce((sum, s) => sum + s.occurrences * s.avgDelayDays, 0)

export const STALL_ROWS: readonly StallRow[] = [...STALL_SEEDS]
  .map((seed) => {
    const delayDays = seed.occurrences * seed.avgDelayDays
    return { ...seed, delayDays, sharePct: Math.round((delayDays / TOTAL_DELAY_DAYS) * 100) }
  })
  .sort((a, b) => b.delayDays - a.delayDays)

export const STALL_LABEL = `Why work stalls · last ${WINDOW_DAYS} days`
export const STALL_CAPTION = `Reasons work stalled across the portfolio in the last ${WINDOW_DAYS} days, with the days each reason cost and who owns the fix.`

export const STALL_COLUMNS = [
  { key: 'reason', label: 'Reason', numeric: false },
  { key: 'occurrences', label: 'Occurrences', numeric: true },
  { key: 'avgDelay', label: 'Avg delay', numeric: true },
  { key: 'delayDays', label: 'Delay days', numeric: true },
  { key: 'owner', label: 'Owner', numeric: false },
] as const

export const TOTALS = {
  occurrences: STALL_ROWS.reduce((sum, r) => sum + r.occurrences, 0),
  delayDays: TOTAL_DELAY_DAYS,
  clientDelayDays: STALL_ROWS.filter((r) => r.owner === 'Client').reduce((sum, r) => sum + r.delayDays, 0),
} as const

export const TOTALS_LABEL = 'All reasons'
export const NO_FIX_SURFACE_NOTE = 'no single surface owns this one'

/* ── the computed conclusion (the page's most salient statement) ─────────── */

const CLIENT_SHARE_PCT = Math.round((TOTALS.clientDelayDays / TOTALS.delayDays) * 100)
/** 80% written the way the group says it out loud: "4 of every 5". */
const CLIENT_SHARE_OF_FIVE = Number(((CLIENT_SHARE_PCT / 100) * 5).toFixed(1))

export const CONCLUSION = {
  headline: `${CLIENT_SHARE_OF_FIVE} of every 5 delay days sit with the client`,
  arithmetic: `${thinSpace(TOTALS.clientDelayDays)} of ${thinSpace(TOTALS.delayDays)} delay days, across ${thinSpace(
    TOTALS.occurrences
  )} stalls — ${CLIENT_SHARE_PCT}% client-owned.`,
  insight:
    'When the patterned middle is compressed by agents, the critical path moves to client decisions: the work we control is no longer what sets the date.',
  cta: { before: 'which is the argument for ', link: { label: 'the client portal', to: ROUTES.portal } },
} as const

/* ── KPI row ────────────────────────────────────────────────────────────── */

export interface HealthKpi {
  key: string
  label: string
  figure: string
  qualifier: string
}

/** Deck figures the series cannot derive, transcribed once. */
const GATES_ON_TIME_PCT = 84
const SCOPE_CHANGE_REQUESTS = 31
const SCOPE_PRICED_FAST_PCT = 78
const SCOPE_PRICING_HOURS = 48
const REWORK_PCT = 4.1
const REWORK_BENCHMARK_PCT = 12

export const HEALTH_KPIS: readonly HealthKpi[] = [
  {
    key: 'avg-days',
    label: 'Avg days per phase',
    figure: String(AVG_DAYS_NOW),
    qualifier: `was ${AVG_DAYS_BEFORE}`,
  },
  {
    key: 'on-time-gates',
    label: 'On-time gates',
    figure: `${GATES_ON_TIME_PCT}%`,
    qualifier: `across ${PORTFOLIO.active} active engagements`,
  },
  {
    key: 'scope-changes',
    label: 'Scope change requests',
    figure: String(SCOPE_CHANGE_REQUESTS),
    qualifier: `${SCOPE_PRICED_FAST_PCT}% priced within ${SCOPE_PRICING_HOURS} h`,
  },
  {
    key: 'rework',
    label: 'Rework after go-live',
    figure: `${REWORK_PCT}%`,
    qualifier: `benchmark ~${REWORK_BENCHMARK_PCT}%`,
  },
]

export const KPI_SECTION_LABEL = `Last ${WINDOW_DAYS} days`

/* ── density note ───────────────────────────────────────────────────────── */

export const DENSITY_NOTE = {
  before: `Figures aggregate the ${PORTFOLIO.active} active engagements; per-project drill-down is `,
  link: { label: `the board`, to: ROUTES.board },
  after: ` (${PROJECTS.length} seeded rows). Gate-level history is withheld this round — the ${WINDOW_DAYS}-day medians and the stall register carry the argument without it.`,
} as const
